import type { SortDir } from "@/components/ui/sortable-head";
import { useMemo, useState } from "react";

type SortValue = string | number | boolean | Date | null | undefined;

/**
 * Click-to-sort for a list the page already holds in full. Pairs with
 * `<SortableHead>`: first click on a column uses its natural direction, the second
 * flips. Nulls sort last whichever way.
 */
export function useClientSort<T, K extends string>(
  rows: readonly T[],
  columns: Record<K, { value: (row: T) => SortValue; firstDir?: SortDir }>,
  initial?: { key: NoInfer<K>; dir: SortDir } | null,
) {
  const [sort, setSort] = useState<{ key: K; dir: SortDir } | null>(initial ?? null);

  const onSort = (key: K) =>
    setSort((prev) =>
      prev?.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: columns[key].firstDir ?? "asc" },
    );

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const { value } = columns[sort.key];
    const sign = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = norm(value(a));
      const bv = norm(value(b));
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      if (typeof av === "string" && typeof bv === "string") {
        return av.localeCompare(bv, undefined, { sensitivity: "base" }) * sign;
      }
      return (av < bv ? -1 : av > bv ? 1 : 0) * sign;
    });
  }, [rows, sort, columns]);

  return { sorted, sortKey: sort?.key ?? null, sortDir: sort?.dir ?? "asc", onSort };
}

function norm(v: SortValue): string | number | null {
  if (v === null || v === undefined || v === "") return null;
  if (v instanceof Date) return v.getTime();
  if (typeof v === "boolean") return v ? 1 : 0;
  return v;
}
